import WRAP from "WRAP-JS";
import * as ConfigTC from "./config/ConfigTropicalCyclone";
import * as FPEUtils from "../../../utils/FPEUtils";
import { formatLonLatDM, formatTimeZoneDate } from "../../../utils/FPEUtils";
import LegendTropicalCyclone from "../legend/LegendTropicalCyclone";
import { WX_ID_TROPICALCYCLONE } from "../../../store/WeatherContentsState";
import Mutex from "../../../utils/Mutex";
import { COMMUNICATE_ERROR_MESSAGE_TROPICAL_CYCLONE, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER } from "../../../constants/ErrorMessage";

export const TROPICALCYCLONE_TYPE_WNI_GLOBAL_TC = "WNIGlobalTC";
export const TROPICALCYCLONE_TYPE_JMA = "JMA";
export const TROPICALCYCLONE_TYPE_JTWC = "JTWC";

const TROPICAL_STORM_THRESHOLD = 0;
const TYPHOON_THRESHOLD = 12 * 3600 * 1000;

class TropicalCyclone {

  static NAME = WX_ID_TROPICALCYCLONE;

  // WNI Global TC
  dhTC;
  layerTC;

  // JMA
  dhJMA;
  layerJMA;

  // JTWC
  dhJTWC;
  layerJTWC;

  dhAutoUpdate = {
    [TROPICALCYCLONE_TYPE_WNI_GLOBAL_TC]: true,
    [TROPICALCYCLONE_TYPE_JMA]: true,
    [TROPICALCYCLONE_TYPE_JTWC]: true,
  };

  areaInitialized = {
    [TROPICALCYCLONE_TYPE_WNI_GLOBAL_TC]: false,
    [TROPICALCYCLONE_TYPE_JMA]: false,
    [TROPICALCYCLONE_TYPE_JTWC]: false,
  };

  areaVisible = {
    [TROPICALCYCLONE_TYPE_WNI_GLOBAL_TC]: false,
    [TROPICALCYCLONE_TYPE_JMA]: false,
    [TROPICALCYCLONE_TYPE_JTWC]: false,
  }

  issueTimeInfo = {
    [TROPICALCYCLONE_TYPE_WNI_GLOBAL_TC]: '---',
    [TROPICALCYCLONE_TYPE_JMA]: '---',
    [TROPICALCYCLONE_TYPE_JTWC]: '---',
  }

  ready = false;
  visible;
  dispatcher;
  exDispatcher;
  language;
  errorMessageInfo = {};

  constructor() {
    this.layerTC = new WRAP.Geo.Layer('TROPICAL_CYCLONE');
    this.layerTC.setVisible(false);

    this.layerJMA = new WRAP.Geo.Layer('TROPICAL_CYCLONE_JMA');
    this.layerJMA.setVisible(false);

    this.layerJTWC = new WRAP.Geo.Layer('TROPICAL_CYCLONE_JTWC');
    this.layerJTWC.setVisible(false);

    this.drawLayerMutex = new Mutex();
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  setExDispatcher(dispatcher) {
    this.exDispatcher = dispatcher;
  }

  async initialize(dataType) {
    try {
      await this.initData(dataType);
      this.initLayer(dataType);
      this.ready = true;
    } catch {
      console.log('cyclone load error');
    }
  }

  initData(dataType) {
    return new Promise((resolve, reject) => {
      let config;
      let dh;

      switch (dataType) {
        case TROPICALCYCLONE_TYPE_WNI_GLOBAL_TC:
          config = ConfigTC.configCycloneDhWniIndex;
          config.Source.index.updated = this._filterTropicalStorm;
          dh = this.dhTC = new WRAP.Data(config);
          break;
        case TROPICALCYCLONE_TYPE_JMA:
          config = ConfigTC.configCycloneDhJMAIndex;
          config.Source.index.updated = this._filterTyphoon;
          dh = this.dhJMA = new WRAP.Data(config);
          break;
        case TROPICALCYCLONE_TYPE_JTWC:
          config = ConfigTC.configCycloneDhJTWCIndex;
          config.Source.index.updated = this._filterTropicalStorm;
          dh = this.dhJTWC = new WRAP.Data(config);
          break;
        default:
      }

      if (dh) {
        dh.updated('index', ref => {
          if (this.errorMessageInfo[`${dataType}_index`]) {
            this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo[`${dataType}_index`]});
            this.errorMessageInfo[`${dataType}_index`] = null;
          }

          this.areaInitialized[dataType] = true;
          resolve(ref);
        }, () => {
          this.errorMessageInfo[`${dataType}_index`] = this.dispatcher({type: 'SHOW_ERROR', payload: {
            message: COMMUNICATE_ERROR_MESSAGE_TROPICAL_CYCLONE,
            replacement: {data: 'index'},
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
          }});
          reject();
        });

        dh.updated('data', ref => {
          if (dh.status.data === 'data load error.') {
            if (this.errorMessageInfo[`${dataType}_data`]) {
              this.dispatcher({type: 'SHOW_ERROR', payload: this.errorMessageInfo[`${dataType}_data`]});
              this.errorMessageInfo[`${dataType}_data`] = null;
            }
            return;
          }
          this.errorMessageInfo[`${dataType}_data`] = this.dispatcher({type: 'CLEAR_ERROR', payload: {
            message: COMMUNICATE_ERROR_MESSAGE_TROPICAL_CYCLONE,
            replacement: {data: 'element'},
            additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
          }});
          this._selectBaseValidTime(ref, dataType);
          this.exDispatcher((oldValue) => {
            return { ...oldValue, data: { ...oldValue.data, [dataType]: FPEUtils.deepCopy(ref) } };
          });
        });
      }
    });
  }

  initLayer(dataType) {
    const target = this._getTarget(dataType);
    if (Array.isArray(target.layer)){
      for (let i = 0; i < target.layer.length; i++) {
        target.layer[i].configure(target.dh, target.layerConfig[i]);
        if (target.tooltip) {
          target.layer[i].setTooltip(target.tooltip);
        }
      }
    } else if (target.layer) {
      target.layer.configure(target.dh, target.layerConfig);
      if (target.tooltip) {
        target.layer.setTooltip(target.tooltip);
      }
    }
    this._setLanguage(target.layer, target.layerConfig);
  }

  initInteraction() {
    WRAP.Geo.addEventHandler('mouseover', (layer, feature, sp) => {
      if (layer === this.layerTC) {
        if (feature.geo) {
          const conf = ConfigTC.configCycloneWniLayer.Attributes;
          const sameFeatures = layer._dl.filter(f => f.geo === feature.geo);
          sameFeatures.forEach(f => {
            const style = f.style();
            style.lineWidth = conf.wind_highlight_line_width || style.lineWidth;
          });
          // WebGLの再描画条件
          layer._immediate = true;
          layer._modified = true;
          WRAP.Geo.invalidate();
        }
      } else if (layer === this.layerJTWC) {
        if (feature.geo) {
          const conf = ConfigTC.configCycloneJTWCLayer.Attributes;
          const sameFeatures = layer._dl.filter(f => f.geo === feature.geo);
          sameFeatures.forEach(f => {
            const style = f.style();
            style.lineWidth = conf.wind_highlight_line_width || style.lineWidth;
          });
          // WebGLの再描画条件
          layer._immediate = true;
          layer._modified = true;
          WRAP.Geo.invalidate();
        }
      } else if (layer === this.layerJMA) {
      }
    });

    WRAP.Geo.addEventHandler('mouseout', (layer, feature, sp) => {
      if (layer === this.layerTC) {
        if (feature.geo) {
          const conf = ConfigTC.configCycloneWniLayer.Attributes;
          const sameFeatures = layer._dl.filter(f => f.geo === feature.geo);
          sameFeatures.forEach(f => {
            const style = f.style();
            style.lineWidth = conf.wind_line_width || style.lineWidth;
          });
          // WebGLの再描画条件
          layer._immediate = true;
          layer._modified = true;
          WRAP.Geo.invalidate();
        }
      } else if (layer === this.layerJTWC) {
        if (feature.geo) {
          const conf = ConfigTC.configCycloneJTWCLayer.Attributes;
          const sameFeatures = layer._dl.filter(f => f.geo === feature.geo);
          sameFeatures.forEach(f => {
            const style = f.style();
            style.lineWidth = conf.wind_line_width || style.lineWidth;
          });
          // WebGLの再描画条件
          layer._immediate = true;
          layer._modified = true;
          WRAP.Geo.invalidate();
        }
      } else if (layer === this.layerJMA) {
      }
    });
  }

  async drawLayer(visible, type) {
    this.drawLayerMutex.runExclusion(async () => {
      if (type) {
        this.areaVisible[type] = visible;
        this.visible = Object.values(this.areaVisible).some(v => v);
      } else {
        type = this._visibleArea();
        this.visible = visible;
      }
      this.exDispatcher(oldValue => {
        return { ...oldValue, visible: {...this.areaVisible} };
      });

      for (const t of [].concat(type)) {
        if (visible && !this.areaInitialized[t]) {
          await this.initialize(t);
        }
        this.setVisible(visible, t);
      }
    });
  }

  getLayer() {
    return this._allLayers();
  }

  setVisible(visible, type) {
    if (!this.areaInitialized[type]) {
      return;
    }

    const target = this._getTarget(type);

    if (Array.isArray(target.layer)) {
      if (target.layer[0].visible() === visible) {
        return;
      }

      for (let i = 0; i < target.layer.length; i++) {
        if (visible) {
          if (!this.coverage && target.layer[i]._conf.Attributes.layer_type === 'coverage') {
            continue;
          }
          target.layer[i].show();
        } else {
          target.layer[i].hide();
        }
      }

    } else if (target.layer) {
      if (target.layer.visible() === visible) {
        return;
      }

      if (visible) {
        target.layer.show();
      } else {
        target.layer.hide();
      }
    }

    const enabled = visible && this.autoRefresh;
    if (target.autoUpdate !== enabled) {
      target.dh.setAutoUpdate('index', enabled);
      this.dhAutoUpdate[type] = enabled;
    }

    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});

  }

  isVisible(type) {
    return this.areaVisible[type];
  }

  getTimeInformation() {
    const sortInfo = {
      [TROPICALCYCLONE_TYPE_WNI_GLOBAL_TC]: 1,
      [TROPICALCYCLONE_TYPE_JMA]: 3,
      [TROPICALCYCLONE_TYPE_JTWC]: 4,
    }
    const timeList = this._visibleArea().map(t => {
      return {type: t, time: this.issueTimeInfo[t]};
    }).sort((a, b) => {
      return sortInfo[a.type] - sortInfo[b.type];
    });

    return {
      id: TropicalCyclone.NAME,
      label: 'Typhoon',
      baseTime: timeList,
      baseTimeFormatter: time => {
        return `${time.type} ${formatTimeZoneDate(time.time)}`
      }
    }
  }

  getLegend() {
    return {
      id: TropicalCyclone.NAME,
      label: 'Typhoon',
      component: LegendTropicalCyclone,
      contents: this
    };
  }

  changeRoutePointEditing(routePointEditing) {
    this._visibleArea().forEach(v=> {
      const target = this._getTarget(v);
      target.layer.disableInteraction(routePointEditing);
    });
  }

  changeLanguage(language, t) {
    this._visibleArea().forEach(v=> {
      const target = this._getTarget(v);
      this._setLanguage(target.layer, target.layerConfig)
    });
  }

  changeTimeZone(tz) {
    this._visibleArea().forEach(v=> {
      const target = this._getTarget(v);
      target.layer.invalidate();
    });
  }

  _filterTropicalStorm(ref) {
    if (ref?.index) {
      const now = Date.now();
      Object.keys(ref.index).forEach(id => {
        const tc = ref.index[id];
        if (tc.final_flag > 0 && tc.valid_end && FPEUtils.parseUTCDate(tc.valid_end).getTime() + TROPICAL_STORM_THRESHOLD < now) {
          delete ref.index[id];
        }
      });
    }
  }

  _filterTyphoon(ref) {
    if (ref?.index) {
      const now = Date.now();
      Object.keys(ref.index).forEach(id => {
        const tc = ref.index[id];
        if (tc.final_flag > 0 && tc.issue_date && FPEUtils.parseUTCDate(tc.issue_date).getTime() + TYPHOON_THRESHOLD < now) {
          delete ref.index[id];
        }
      });
    }
  }

  _setLanguage(layer, layerConfig) {
    const layers = [].concat(layer);
    const layerConfigs = [].concat(layerConfig);
    for (let i = 0; i < layers.length; i++) {
      const l = layers[i];
      const c = layerConfigs[i];
      const language = FPEUtils.language();
      if (!c.language || !c.language[language]) {
        continue;
      }
      l.setAttributes(c.language[language]);
    }
  }

  _visibleArea() {
    return Object.keys(this.areaVisible).sort().reduce((visibleAreaList, area) => {
      if (this.areaVisible[area]) {
        visibleAreaList.push(area)
      }
      return visibleAreaList;
    }, []);
  }

  _getTarget(dataType) {
    let dh;
    let layer;
    let layerConfig;
    let tooltip;
    switch (dataType) {
      case TROPICALCYCLONE_TYPE_WNI_GLOBAL_TC:
        dh = this.dhTC;
        layer = this.layerTC;
        layerConfig = ConfigTC.configCycloneWniLayer;
        tooltip = this._tooltip;
        break;
      case TROPICALCYCLONE_TYPE_JMA:
        dh = this.dhJMA;
        layer = this.layerJMA;
        layerConfig = ConfigTC.configCycloneJMALayer;
        tooltip = this._tooltipJMA;
        break;
      case TROPICALCYCLONE_TYPE_JTWC:
        dh = this.dhJTWC;
        layer = this.layerJTWC;
        layerConfig = ConfigTC.configCycloneJTWCLayer;
        tooltip = this._tooltip;
        break;
      default:
    }
    return {
      dh: dh,
      autoUpdate: this.dhAutoUpdate[dataType],
      layer: layer,
      layerConfig: layerConfig,
      tooltip: tooltip,
    }
  }

  _allLayers() {
    return [
      this.layerTC,
      this.layerJMA,
      this.layerJTWC,
    ];
  }

  _selectBaseValidTime(ref, dataType) {
    const maxIssueTime = Object.entries(ref).reduce((maxIssue, [id, tc]) => {
      if (dataType === TROPICALCYCLONE_TYPE_JMA) {
        return maxIssue < tc['analyzed_date'] ? tc['analyzed_date'] : maxIssue;
      } else if (dataType === TROPICALCYCLONE_TYPE_JTWC) {
        return maxIssue < tc['announced'] ? tc['announced'] : maxIssue;
      } else if (dataType === TROPICALCYCLONE_TYPE_WNI_GLOBAL_TC) {
        const tmp = tc.features
          .filter(f => f.properties.wrap_feature_type === 'analysis')
          .map(f => f.properties.valid)
          .sort((a, b) => b.localeCompare(a))
          .slice(0)[0];
        return maxIssue < tmp ? tmp : maxIssue;
      }
      return maxIssue;
    }, this.issueTimeInfo[dataType]);

    if (this.issueTimeInfo[dataType] !== maxIssueTime) {
      this.issueTimeInfo[dataType] = maxIssueTime;
      this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
    }
  }

  _tooltip(geo) {
    if (!geo) return;

    const p = geo.properties;

    if (!p) return;

    const name = p.ename && p.ename !== '_missing_' ? p.ename : '---';
    const type = p.wrap_feature_type === 'forecast' ? FPEUtils.translation('tc_forecast') : FPEUtils.translation('tc_analysis');
    const valid = p.valid ? formatTimeZoneDate(p.valid, undefined, 'DD / HHz') : '---';
    const position = formatLonLatDM(geo.geometry.coordinates[0], geo.geometry.coordinates[1]);
    const pressure = p.pressure && p.pressure !== '_missing_' ? `${p.pressure}hPa` : '---';
    const dir = p.heading && p.heading !== '_missing_' ? `${p.heading}&deg;` : '---';
    const speed = p.speed && p.speed !== '_missing_' ? `${p.speed}KT` : '---';
    const maxSpeed = p.max_wind_speed && p.max_wind_speed !== '_missing_' ? `${p.max_wind_speed}KT` : '---';
    const gust = p.gusts && p.gusts !== '_missing_' ? `${p.gusts}KT` : '---';
    // const maxGust = `${maxSpeed} / ${gust}`;

    let max35 = [p.stmrds_gale_1, p.stmrds_gale_2, p.stmrds_gale_3, p.stmrds_gale_4].reduce((maxGale, gale) => {
      return maxGale < gale ? gale : maxGale;
    }, -1);

    let max50 = [p.stmrds_storm_1, p.stmrds_storm_2, p.stmrds_storm_3, p.stmrds_storm_4].reduce((maxStorm, storm) => {
      return maxStorm < storm ? storm : maxStorm;
    }, -1);

    const text = [];
    text.push(`<ul style="text-align: left;">
      <li>${name}</li>
      <li>${type} : ${valid}</li>
      <li>${FPEUtils.translation('tc_position')} : ${position}</li>
      <li>${FPEUtils.translation('tc_pressure')} : ${pressure}</li>
      <li>${FPEUtils.translation('tc_dir')} : ${dir}</li>
      <li>${FPEUtils.translation('tc_speed')} : ${speed}</li>
      <li>${FPEUtils.translation('Max wind speed')} : ${maxSpeed}</li>
      <li>${FPEUtils.translation('Max gust')} : ${gust}</li>`
    );

    if (max35 !== -1) {
      text.push(`<li>35KT ${FPEUtils.translation('tc_35_radius')} : ${max35}NM</li>`);
    }

    if (max50 !== -1) {
      text.push(`<li>50KT ${FPEUtils.translation('tc_50_radius')} : ${max50}NM</li>`);
    }

    text.push('</ul>');
    return text.join('');
  }


  _tooltipJMA(geo) {
    if (!geo) return;

    const p = geo.properties;

    if (!p) return;

    const name = p.ename && p.ename !== '_missing_' ? p.ename : '---';
    const type = p.wrap_feature_type === 'forecast' ? FPEUtils.translation('tc_forecast') : FPEUtils.translation('tc_analysis');
    const date = (() => {
      switch (p.wrap_feature_type) {
        case 'estimate':
          return p.wrap_estimated_date;
        case 'forecast':
          return p.wrap_forecast_date;
        default:
          return p.analyzed_date;
      }
    })();
    const valid = date ? formatTimeZoneDate(date, undefined, 'DD / HHz') : '---';
    const position = formatLonLatDM(geo.geometry.coordinates[0], geo.geometry.coordinates[1]);
    const pressure = p.state.central_pressure && p.state.central_pressure !== '_missing_' ? `${p.state.central_pressure}hPa` : '---';
    const dir = p.state.moving_direction && p.state.moving_direction !== '_missing_' ? `${FPEUtils.translation(p.state.moving_direction)}` : '---';
    const speed = dir !== 'STR' && p.state.speed_kt && p.state.speed_kt !== '_missing_' ? p.state.speed_kt !== -1111 ? `${p.state.speed_kt}KT` : FPEUtils.translation('tc_slowly') : '---';
    const maxSpeed = p.wind.max_wind_speed_kt && p.wind.max_wind_speed_kt !== '_missing_' ? `${p.wind.max_wind_speed_kt}KT` : '---';
    const gust = p.wind.wind_gust_kt && p.wind.wind_gust_kt !== '_missing_' ? `${p.wind.wind_gust_kt}KT` : '---';

    const text = [];
    text.push(`<ul style="text-align: left;">
      <li>${name}</li>
      <li>${type} : ${valid}</li>
      <li>${FPEUtils.translation('tc_position')} : ${position}</li>
      <li>${FPEUtils.translation('tc_pressure')} : ${pressure}</li>
      <li>${FPEUtils.translation('tc_dir')} : ${dir}</li>
      <li>${FPEUtils.translation('tc_speed')} : ${speed}</li>
      <li>${FPEUtils.translation('Max wind speed')} : ${maxSpeed}</li>
      <li>${FPEUtils.translation('Max gust')} : ${gust}</li>`
    );

    text.push('</ul>');
    return text.join('');
  }
}

export default TropicalCyclone;
